import { ComponentProps, PropsWithChildren, forwardRef, useMemo } from 'react';
import { IconContext } from 'react-icons';
import styles from './button.module.css';

export type ButtonProps = ComponentProps<'button'> & {
    disabled?: boolean;
    focus?: boolean;
    preset?: 'primary' | 'secondary' | 'success';
    textPreset?: 'small' | 'medium' | 'large';
    icon?: React.ReactNode;
    onClick?: () => void;
    className?: string;
    style?: React.CSSProperties;
};

function ButtonComponent(
    {
        preset = 'primary',
        textPreset = 'medium',
        icon,
        onClick,
        className,
        children,
        ...rest
    }: PropsWithChildren<ButtonProps>,
    ref: React.ForwardedRef<HTMLButtonElement>
) {
    const containerClasses = `${styles.button} ${(() => {
        if (preset === 'primary') return styles.primary;
        if (preset === 'secondary') return styles.secondary;
        return styles.success;
    })()} ${rest.focus && styles.focus} ${className}`;

    let contentClasses = `${styles.content} `;

    switch (textPreset) {
        case 'small':
            contentClasses += styles.textSmall;
            break;
        case 'medium':
            contentClasses += styles.textMedium;
            break;
        case 'large':
            contentClasses += styles.textLarge;
            break;
        default:
            contentClasses += styles.textMedium;
            break;
    }

    const onClickAction = () => {
        if (onClick && !rest.disabled) {
            onClick();
        }
    };

    const iconValue = useMemo(() => ({ className: styles.middle, size: '1.4em' }), []);

    return (
        // eslint-disable-next-line react/button-has-type
        <button ref={ref} className={containerClasses} onClick={onClickAction} {...rest}>
            <div className={contentClasses}>
                {icon && (
                    <IconContext.Provider value={iconValue}>
                        <div>{icon}</div>
                    </IconContext.Provider>
                )}

                {icon && children && <div className={styles.space} />}
                <div className={styles.middle}>{children}</div>
            </div>
        </button>
    );
}

/**
 * Button component.
 * @param props The button props.
 * @returns The button component.
 * @example
 * ```tsx
 * import { MdZoomOutMap } from 'react-icons/md';
 * import { Button } from '~/components/ui/button';
 *
 * export function SignIn({ onHome }) {
 *    return (
 *      <Button onClick={onHome} preset="secondary" icon={<MdZoomOutMap />}>
 *         Hello
 *     </Button>
 * )}
 */
export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(ButtonComponent);
